// The one reader of area shapes. Whatever form a shape arrives in (WKT text, the `{ value }` wrapper,
// GeoJSON), it comes out as an AreaShape, or null when it cannot be read. Pure: no React or services.
//
// The WKT reader covers the types Telcofy emits: POINT, LINESTRING, POLYGON, MULTILINESTRING and
// MULTIPOLYGON, 2D only. If we ever need MULTIPOINT, GEOMETRYCOLLECTION or 3D/M coordinates, switch to
// the `wellknown` package.
import type { AreaShape, Position, ShapeInput } from './types';

const NUMBER = '-?\\d+(?:\\.\\d+)?(?:[eE][-+]?\\d+)?';
const NUM_PAIR = new RegExp(`${NUMBER}\\s+${NUMBER}`, 'g');

function parseCoords(chunk: string): Position[] {
  return (chunk.match(NUM_PAIR) || []).map((pair) => {
    const [lng, lat] = pair.split(/\s+/).map(Number);
    return [lng, lat] as Position;
  });
}

const stripOuter = (s: string): string => s.trim().replace(/^\(/, '').replace(/\)$/, '');

function parseRings(body: string): Position[][] {
  // body is what follows POLYGON: ((..,..),(..,..)); split the rings on "),("
  return stripOuter(body).split(/\)\s*,\s*\(/).map(parseCoords);
}

function readWkt(wkt: string): AreaShape | null {
  const match = wkt.trim().match(/^(POINT|LINESTRING|POLYGON|MULTILINESTRING|MULTIPOLYGON)\s*([\s\S]*)$/i);
  if (!match) return null;
  const type = match[1].toUpperCase();
  const body = match[2].trim();

  switch (type) {
    case 'POINT': {
      const [first] = parseCoords(body);
      return first ? { type: 'Point', coordinates: first } : null;
    }
    case 'LINESTRING':
      return { type: 'LineString', coordinates: parseCoords(body) };
    case 'POLYGON':
      return { type: 'Polygon', coordinates: parseRings(body) };
    case 'MULTILINESTRING':
      return { type: 'MultiLineString', coordinates: parseRings(body) };
    case 'MULTIPOLYGON': {
      // body: (((..,..),(..,..)),((..,..)))
      const polygons = stripOuter(body).split(/\)\s*\)\s*,\s*\(\s*\(/);
      return {
        type: 'MultiPolygon',
        coordinates: polygons.map((p) => p.replace(/^\(+|\)+$/g, '').split(/\)\s*,\s*\(/).map(parseCoords)),
      };
    }
    default:
      return null;
  }
}

const isPosition = (c: unknown): c is Position =>
  Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]);

const cleanLine = (line: unknown): Position[] =>
  Array.isArray(line) ? line.filter(isPosition).map((c) => [c[0], c[1]] as Position) : [];

const cleanRings = (rings: unknown): Position[][] =>
  Array.isArray(rings) ? rings.map(cleanLine).filter((r) => r.length > 0) : [];

function readGeoJson(geom: { type?: unknown; coordinates?: unknown }): AreaShape | null {
  const { type, coordinates } = geom;
  if (!Array.isArray(coordinates)) return null;
  switch (type) {
    case 'Point':
      return isPosition(coordinates) ? { type: 'Point', coordinates: [coordinates[0], coordinates[1]] } : null;
    case 'LineString': {
      const line = cleanLine(coordinates);
      return line.length > 0 ? { type: 'LineString', coordinates: line } : null;
    }
    case 'MultiLineString': {
      const lines = cleanRings(coordinates);
      return lines.length > 0 ? { type: 'MultiLineString', coordinates: lines } : null;
    }
    case 'Polygon': {
      const rings = cleanRings(coordinates);
      return rings.length > 0 ? { type: 'Polygon', coordinates: rings } : null;
    }
    case 'MultiPolygon': {
      const polygons = coordinates.map(cleanRings).filter((rings) => rings.length > 0);
      return polygons.length > 0 ? { type: 'MultiPolygon', coordinates: polygons } : null;
    }
    default:
      return null;
  }
}

/** The shape in `input`, or null when it is empty or cannot be read. Never throws. */
export function readShape(input: ShapeInput | unknown): AreaShape | null {
  try {
    if (typeof input === 'string') {
      const shape = readWkt(input);
      return shape ? readGeoJson(shape) : null; // the same clean-up, so a WKT shape is never empty
    }
    if (input && typeof input === 'object') {
      const obj = input as { value?: unknown; geometry?: unknown; type?: unknown; coordinates?: unknown };
      if (obj.type === 'Feature') return readShape(obj.geometry);
      if (typeof obj.type === 'string') return readGeoJson(obj);
      if ('value' in obj) return readShape(obj.value);
    }
  } catch (e) {
    console.warn('Failed to read area shape:', e);
  }
  return null;
}

/** The shapes that can be read among `inputs`, in order. */
export function readShapes(inputs: ReadonlyArray<ShapeInput | unknown>): AreaShape[] {
  return inputs.flatMap((input) => {
    const shape = readShape(input);
    return shape ? [shape] : [];
  });
}

/**
 * The outlines of a shape as rings of [lng, lat], for drawing: every ring of a polygon, every polygon of
 * a multipolygon, every line. A point has no outline.
 */
export function outlineRings(shape: AreaShape): Position[][] {
  switch (shape.type) {
    case 'Polygon':
    case 'MultiLineString':
      return shape.coordinates.filter((r) => r.length >= 2);
    case 'MultiPolygon':
      return shape.coordinates.flat().filter((r) => r.length >= 2);
    case 'LineString':
      return shape.coordinates.length >= 2 ? [shape.coordinates] : [];
    default:
      return [];
  }
}

/** Every coordinate of the shapes. */
export function positionsOf(shapes: readonly AreaShape[]): Position[] {
  return shapes.flatMap((s) => {
    switch (s.type) {
      case 'Point':
        return [s.coordinates];
      case 'LineString':
        return s.coordinates;
      case 'MultiLineString':
      case 'Polygon':
        return s.coordinates.flat();
      case 'MultiPolygon':
        return s.coordinates.flat(2);
      default:
        return [];
    }
  });
}
